import {ApiError, api} from './client';

// Resumable chunked uploader. Контракт із бекендом — три ендпойнти:
//   POST <createSession>          (Idempotency-Key) → створити або повернути
//        існуючу сесію; повертає {sessionId, receivedBytes, completed}.
//   GET  <sessionStatus(id)>      → справжній offset з боку Drive.
//   PUT  <chunkPut(id)>           → один чанк з Content-Range; повертає
//        {completed, receivedBytes, driveFileId?}.
//
// Шляхи передаються через `endpoint` — це дозволяє тому ж пайплайну обслуговувати
// і основний (/api/upload/...) і тимчасовий публічний (/api/_test/upload/...).
// Drive вимагає щоб non-final чанк був кратним 256 KB; final може бути
// будь-якого розміру. Тримаємо 8 MB — компроміс між кількістю round-trip і
// часом перезаливу одного чанка на повільному мобільному каналі.
const CHUNK_BYTES = 8 * 1024 * 1024;
const KB256 = 256 * 1024;

if (CHUNK_BYTES % KB256 !== 0) {
  throw new Error('CHUNK_BYTES must be a multiple of 256 KB');
}

export interface UploadProgress {
  loaded: number;
  total: number;
}

export interface AttemptInfo {
  attempt: number; // 1-based, без верхньої межі — ретраїмо доки є прогрес
  online: boolean; // false = чекаємо на повернення мережі
}

export interface ResumableUploadResult {
  driveFileId: string;
  filename: string;
  sizeBytes: number;
  mimeType: string;
}

// Шляхи ресурсів resumable-сесії. createSession і sessionStatus — без префіксу
// `/api` (їх зове `api()` хелпер, який сам додає базу). chunkPut — повний шлях
// з `/api/`, бо XHR.open() не знає про базу.
//
// Direct-upload mode: сервер на POST /sessions повертає driveSessionUrl —
// клієнт ллє чанки напряму на googleapis.com, а після успіху викликає
// `finalize` ендпойнт нашого сервера. `chunkPut` у цьому режимі не юзається
// і може бути пропущений.
export interface ResumableEndpoint {
  createSession: string;
  sessionStatus: (sessionId: string) => string;
  // Тільки proxy-mode. У direct-mode сервер не отримує чанки взагалі.
  chunkPut?: (sessionId: string) => string;
  // Тільки direct-mode. Викликаємо коли Drive віддав 200/201 — наш сервер
  // верифікує driveFileId і пише upload_log.
  finalize?: (sessionId: string) => string;
}

// Всі чотири підсистеми задають ОБИДВА `finalize` і `chunkPut` — режим обирає
// бекенд per-request (geoip → useDirectForRequest у resumableUpload.ts). За
// дефолтом direct: POST /sessions повертає driveSessionUrl, чанки летять на
// googleapis.com напряму, на 200 від Drive викликаємо /finalize. Для IP з
// заблокованих країн (РФ) бекенд не повертає driveSessionUrl — клієнт
// автоматично іде через `chunkPut` (наш сервер проксить байти у Drive).
//
// chunkPut містить ПОВНИЙ шлях з префіксом `/api/`, бо XHR.open() не знає
// про API_BASE. createSession/sessionStatus/finalize — без `/api`, бо їх зове
// helper `api()` який сам додає базу.
export const mainResumableEndpoint = (anketaId: number): ResumableEndpoint => ({
  createSession: `/upload/sessions?anketaId=${anketaId}`,
  sessionStatus: (id) => `/upload/sessions/${id}`,
  chunkPut: (id) => `/api/upload/sessions/${id}`,
  finalize: (id) => `/upload/sessions/${id}/finalize`,
});

// TEMP: публічний /test ллє у спільну папку TEST. Видалити разом з фронтовим
// /test роутом.
export const testResumableEndpoint: ResumableEndpoint = {
  createSession: '/_test/upload/sessions',
  sessionStatus: (id) => `/_test/upload/sessions/${id}`,
  chunkPut: (id) => `/api/_test/upload/sessions/${id}`,
  finalize: (id) => `/_test/upload/sessions/${id}/finalize`,
};

// Кастоми/контент-реквести: ендпойнти прив'язані до конкретного customRequestId
// /contentRequestId. Auth — Bearer-токен (передається у `extraHeaders` опції
// uploadFileResumable).
export const customResumableEndpoint = (customRequestId: string): ResumableEndpoint => ({
  createSession: `/v1/custom-requests/${encodeURIComponent(customRequestId)}/sessions`,
  sessionStatus: (id) => `/v1/custom-requests/${encodeURIComponent(customRequestId)}/sessions/${id}`,
  chunkPut: (id) => `/api/v1/custom-requests/${encodeURIComponent(customRequestId)}/sessions/${id}`,
  finalize: (id) =>
    `/v1/custom-requests/${encodeURIComponent(customRequestId)}/sessions/${id}/finalize`,
});

export const contentResumableEndpoint = (contentRequestId: string): ResumableEndpoint => ({
  createSession: `/v1/content-requests/${encodeURIComponent(contentRequestId)}/sessions`,
  sessionStatus: (id) =>
    `/v1/content-requests/${encodeURIComponent(contentRequestId)}/sessions/${id}`,
  chunkPut: (id) =>
    `/api/v1/content-requests/${encodeURIComponent(contentRequestId)}/sessions/${id}`,
  finalize: (id) =>
    `/v1/content-requests/${encodeURIComponent(contentRequestId)}/sessions/${id}/finalize`,
});

export interface ResumableUploadOptions {
  file: File;
  endpoint: ResumableEndpoint;
  idempotencyKey: string;
  onProgress: (p: UploadProgress) => void;
  onAttempt?: (info: AttemptInfo) => void;
  signal?: AbortSignal;
  // Додаткові заголовки для всіх трьох запитів (POST sessions, GET status,
  // PUT chunk). Юзаємо для Bearer-токена у кастомах/контенті — там session-
  // cookie ні до чого, токен прив'язує модель до конкретного запиту.
  extraHeaders?: Record<string, string>;
}

// Бекофф для транзієнтних помилок коли мережа є, але сервер відмовляє
// (5xx/429/timeout). Ретраїмо нескінченно, але не частіше ніж раз на cap.
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 30000;

// Watchdog «тиша»: якщо протягом цього часу не зрушив offset (нічого не
// долилось у Drive) — здаємось і кажемо юзеру. Захист від нескінченних
// ретраїв, які нічого не дають (детермінований 502, мертвий бекенд).
const MAX_NO_PROGRESS_MS = 30 * 60 * 1000;

// Стеля очікування при `navigator.onLine === false`. Ховає edge-кейси, коли
// `online`-подія не firing-ить (iOS captive portals тощо) — раз на 5 хвилин
// все одно прокинемось і спробуємо.
const OFFLINE_WAKE_MS = 5 * 60 * 1000;

export async function uploadFileResumable(
  opts: ResumableUploadOptions,
): Promise<ResumableUploadResult> {
  const total = opts.file.size;

  // Прогрес-watchdog. Зрушується щоразу, як ми реально щось долили (offset
  // зріс). При старті — «зараз», щоб у юзера було повне вікно на першу спробу.
  let lastProgressAt = Date.now();
  let attempt = 0;
  let consecutiveErrors = 0;

  while (true) {
    if (opts.signal?.aborted) throw new DOMException('aborted', 'AbortError');
    attempt += 1;
    opts.onAttempt?.({attempt, online: isOnline()});

    try {
      const session = await ensureSession(opts);

      // Сесія була завершена раніше (наприклад, ми ретраїмо після того як
      // востаннє все долили, але не отримали відповідь від сервера). Drive
      // вже має файл — просто рапортуємо успіх.
      if (session.completed && session.driveFileId) {
        opts.onProgress({loaded: total, total});
        return {
          driveFileId: session.driveFileId,
          filename: opts.file.name,
          sizeBytes: total,
          mimeType: opts.file.type || 'application/octet-stream',
        };
      }

      // На retry-attempt-і запитуємо актуальний offset.
      // proxy-mode: у нашого сервера (він всередині ходить у Drive).
      // direct-mode: напряму у Drive (PUT bytes */total).
      let offset: number;
      if (attempt === 1) {
        offset = session.receivedBytes;
      } else if (session.driveSessionUrl) {
        const probe = await queryDriveOffsetDirect(
          session.driveSessionUrl,
          total,
          opts.signal,
        );
        if (probe.completed && probe.driveFileId) {
          // Drive вже все прийняв (попередня спроба долила до кінця, але ми
          // не встигли отримати фінальну відповідь). Викликаємо finalize
          // нашого сервера і виходимо.
          const finalized = await callFinalize(opts, session.sessionId, probe.driveFileId);
          opts.onProgress({loaded: total, total});
          return {
            driveFileId: finalized.driveFileId,
            filename: opts.file.name,
            sizeBytes: finalized.sizeBytes,
            mimeType: opts.file.type || 'application/octet-stream',
          };
        }
        offset = probe.receivedBytes;
      } else {
        offset = await fetchOffset(opts, session.sessionId);
      }

      // Якщо Drive каже «у мене вже X байт» — це ознака життя, оновлюємо
      // watchdog. Інакше ретрай після відключення міг би засмаджуватись
      // годинами «без прогресу» хоч offset насправді просувається.
      if (offset > 0) lastProgressAt = Date.now();

      opts.onProgress({loaded: offset, total});

      while (offset < total) {
        if (opts.signal?.aborted) throw new DOMException('aborted', 'AbortError');

        const end = Math.min(offset + CHUNK_BYTES, total);
        const blob = opts.file.slice(offset, end);
        const chunkBaseOffset = offset;

        const direct = Boolean(session.driveSessionUrl);
        const chunkUrl = direct
          ? (session.driveSessionUrl as string)
          : (opts.endpoint.chunkPut?.(session.sessionId) ?? '');
        if (!chunkUrl) {
          throw new Error('uploadFileResumable: no chunk URL — endpoint missing chunkPut and server did not return driveSessionUrl');
        }

        const result = await putChunk({
          url: chunkUrl,
          direct,
          blob,
          rangeStart: offset,
          rangeEnd: end - 1,
          totalBytes: total,
          extraHeaders: opts.extraHeaders,
          onChunkProgress: (loadedInChunk) => {
            opts.onProgress({
              loaded: chunkBaseOffset + loadedInChunk,
              total,
            });
          },
          signal: opts.signal,
        });

        if (result.completed && result.driveFileId) {
          // У direct-mode Drive віддав 200 — викликаємо наш /finalize щоб
          // сервер верифікував і записав upload_log.
          if (direct) {
            const finalized = await callFinalize(opts, session.sessionId, result.driveFileId);
            opts.onProgress({loaded: total, total});
            return {
              driveFileId: finalized.driveFileId,
              filename: opts.file.name,
              sizeBytes: finalized.sizeBytes,
              mimeType: opts.file.type || 'application/octet-stream',
            };
          }
          opts.onProgress({loaded: total, total});
          return {
            driveFileId: result.driveFileId,
            filename: opts.file.name,
            sizeBytes: total,
            mimeType: opts.file.type || 'application/octet-stream',
          };
        }

        // Drive міг прийняти менше ніж ми вислали (мережа порізала).
        // Беремо authoritative receivedBytes від сервера як новий offset.
        if (result.receivedBytes <= offset) {
          throw new Error(
            `chunk made no progress: server says receivedBytes=${result.receivedBytes}, offset was ${offset}`,
          );
        }
        offset = result.receivedBytes;
        lastProgressAt = Date.now();
        consecutiveErrors = 0;
      }

      throw new Error('all chunks sent but server did not finalize');
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') throw err;
      if (!isRetryable(err)) throw err;

      const stalledFor = Date.now() - lastProgressAt;
      if (stalledFor > MAX_NO_PROGRESS_MS) {
        throw new ApiError(0, 'upload_stalled', null);
      }

      consecutiveErrors += 1;
      // Якщо ми offline — нема сенсу палити батарею бекоффом, чекаємо
      // `online`-подію зі стелею. Якщо online — значить серверна транзієнтна
      // помилка, експ. бекофф (online-подія може скоротити очікування
      // на випадок коли navigator.onLine брехав).
      const offline = !isOnline();
      opts.onAttempt?.({attempt, online: !offline});
      const cap = offline ? OFFLINE_WAKE_MS : backoffDelay(consecutiveErrors);
      await sleepUntilOnlineOrTimer(cap, opts.signal);
    }
  }
}

interface SessionState {
  sessionId: string;
  receivedBytes: number;
  totalBytes: number;
  completed: boolean;
  driveFileId: string | null;
  // Direct-mode тільки. Якщо сервер віддав це поле — клієнт буде PUT-ити
  // чанки напряму на цей URL замість нашого chunkPut.
  driveSessionUrl: string | null;
}

interface CreateSessionResponse {
  sessionId: string;
  receivedBytes: number;
  totalBytes: number;
  completed: boolean;
  driveFileId: string | null;
  driveSessionUrl?: string;
}

async function ensureSession(opts: ResumableUploadOptions): Promise<SessionState> {
  const res = await api<CreateSessionResponse>(opts.endpoint.createSession, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Idempotency-Key': opts.idempotencyKey,
      // Opt-in для direct-mode: сервер віддасть driveSessionUrl тільки коли
      // бачить цей хедер. Без хедера — proxy mode (через chunkPut). Гарантує,
      // що несвіжий бандл (де finalize ще не в фабриках) не отримає direct і
      // не впаде з "endpoint.finalize is missing".
      'X-Resumable-Direct': '1',
      ...opts.extraHeaders,
    },
    body: JSON.stringify({
      filename: opts.file.name,
      mimeType: opts.file.type || 'application/octet-stream',
      totalBytes: opts.file.size,
    }),
    signal: opts.signal ?? null,
  });
  return {
    sessionId: res.sessionId,
    receivedBytes: res.receivedBytes,
    totalBytes: res.totalBytes,
    completed: res.completed,
    driveFileId: res.driveFileId,
    driveSessionUrl: res.driveSessionUrl ?? null,
  };
}

// У proxy-mode питаємо offset у нашого сервера (він всередині ходить у Drive).
// У direct-mode клієнт ходить у Drive напряму через `queryDriveOffsetDirect`.
async function fetchOffset(
  opts: ResumableUploadOptions,
  sessionId: string,
): Promise<number> {
  const res = await api<{
    receivedBytes: number;
    totalBytes: number;
    completed: boolean;
    driveFileId: string | null;
  }>(opts.endpoint.sessionStatus(sessionId), {
    signal: opts.signal ?? null,
    ...(opts.extraHeaders ? {headers: opts.extraHeaders} : {}),
  });
  return res.receivedBytes;
}

// Direct-mode: викликаємо наш сервер щоб він верифікував driveFileId через
// drive.files.get і записав upload_log / зробив побічні ефекти підсистеми.
// Це ОБОВ'ЯЗКОВО — без цього виклику фай є у Drive, але у нашій БД його не буде.
async function callFinalize(
  opts: ResumableUploadOptions,
  sessionId: string,
  driveFileId: string,
): Promise<{driveFileId: string; sizeBytes: number}> {
  if (!opts.endpoint.finalize) {
    throw new Error('uploadFileResumable: direct mode but endpoint.finalize is missing');
  }
  return await api<{completed: boolean; driveFileId: string; sizeBytes: number}>(
    opts.endpoint.finalize(sessionId),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...opts.extraHeaders,
      },
      body: JSON.stringify({driveFileId}),
      signal: opts.signal ?? null,
    },
  );
}

// Direct-mode: питаємо offset напряму у Drive (PUT з Content-Range: bytes */total,
// без тіла). Drive віддасть 308 + Range: bytes=0-N (inclusive), або 200/201
// якщо все вже залите. На 200/201 повертаємо total — клієнт викличе /finalize.
async function queryDriveOffsetDirect(
  driveSessionUrl: string,
  totalBytes: number,
  signal?: AbortSignal,
): Promise<{receivedBytes: number; completed: boolean; driveFileId: string | null}> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('aborted', 'AbortError'));
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', driveSessionUrl);
    xhr.setRequestHeader('Content-Range', `bytes */${totalBytes}`);
    xhr.onload = () => {
      if (xhr.status === 308) {
        const rangeHeader = xhr.getResponseHeader('Range');
        const receivedBytes = parseRangeEnd(rangeHeader) + 1;
        resolve({receivedBytes, completed: false, driveFileId: null});
        return;
      }
      if (xhr.status === 200 || xhr.status === 201) {
        const meta = parseJsonResponse(xhr.responseText);
        const driveFileId = typeof meta.id === 'string' ? meta.id : null;
        resolve({receivedBytes: totalBytes, completed: true, driveFileId});
        return;
      }
      // 404/410 від Drive = сесія померла. Мапимо у session_gone — клієнт
      // створить нову через ensureSession (з новим idempotencyKey якщо потрібно).
      if (xhr.status === 404 || xhr.status === 410) {
        reject(new ApiError(410, 'session_gone', null));
        return;
      }
      reject(new ApiError(xhr.status, 'drive_status_error', xhr.responseText));
    };
    xhr.onerror = () => reject(new ApiError(0, 'network_error', null));
    xhr.ontimeout = () => reject(new ApiError(0, 'timeout', null));
    xhr.onabort = () => reject(new DOMException('aborted', 'AbortError'));
    if (signal) signal.addEventListener('abort', () => xhr.abort(), {once: true});
    // Порожнє тіло: для PUT без чанка Drive чекає Content-Length: 0.
    // Не передаємо body — undici/XHR самі поставлять 0.
    xhr.send();
  });
}

// Drive віддає `Range: bytes=0-N` (inclusive end). Парсимо N.
function parseRangeEnd(rangeHeader: string | null): number {
  if (!rangeHeader) return -1;
  const match = /bytes=\d+-(\d+)/.exec(rangeHeader);
  if (!match) return -1;
  return Number(match[1]);
}

function parseJsonResponse(text: string): {id?: string; size?: string} {
  try {
    return JSON.parse(text) as {id?: string; size?: string};
  } catch {
    return {};
  }
}

interface PutChunkArgs {
  // Куди PUT-имо: повний URL googleapis.com (direct-mode) або наш chunkPut
  // (proxy-mode). Логіка XHR — однакова.
  url: string;
  // Чи це cross-origin PUT до Drive. У proxy-mode (наш сервер) ставимо
  // withCredentials=true і шлемо extraHeaders (Bearer). У direct-mode — НІ
  // (Drive не приймає cookies, а наш Bearer нерелевантний).
  direct: boolean;
  blob: Blob;
  rangeStart: number;
  rangeEnd: number;
  totalBytes: number;
  extraHeaders?: Record<string, string> | undefined;
  onChunkProgress: (loadedInChunk: number) => void;
  signal: AbortSignal | undefined;
}

interface PutChunkResponse {
  completed: boolean;
  receivedBytes: number;
  totalBytes: number;
  driveFileId: string | null;
}

// XHR замість fetch — потрібен upload.onprogress для real-time прогресу.
//
// proxy-mode: наш сервер відповідає JSON {completed, receivedBytes, driveFileId}.
// direct-mode: Drive відповідає 308 (incomplete) з Range-хедером або 200/201
//   (final) з JSON-метаданими файла. Маппимо обидва формати в PutChunkResponse.
function putChunk(args: PutChunkArgs): Promise<PutChunkResponse> {
  return new Promise((resolve, reject) => {
    if (args.signal?.aborted) {
      reject(new DOMException('aborted', 'AbortError'));
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open('PUT', args.url);
    if (!args.direct) {
      // Cookie auth актуальна тільки для нашого сервера (same-origin / CORS
      // з credentials). Drive ігнорує cookies — URL сам по собі credential.
      xhr.withCredentials = true;
      xhr.responseType = 'json';
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    }
    xhr.setRequestHeader(
      'Content-Range',
      `bytes ${args.rangeStart}-${args.rangeEnd}/${args.totalBytes}`,
    );
    if (args.extraHeaders && !args.direct) {
      for (const [k, v] of Object.entries(args.extraHeaders)) {
        xhr.setRequestHeader(k, v);
      }
    }

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) args.onChunkProgress(e.loaded);
    };
    xhr.onload = () => {
      if (args.direct) {
        // 308 → incomplete, читаємо Range
        if (xhr.status === 308) {
          const rangeHeader = xhr.getResponseHeader('Range');
          const receivedBytes = parseRangeEnd(rangeHeader) + 1;
          resolve({
            completed: false,
            receivedBytes,
            totalBytes: args.totalBytes,
            driveFileId: null,
          });
          return;
        }
        // 200/201 → final, JSON-метадані файла. responseType не виставляли,
        // тож читаємо текст і парсимо вручну (відомий формат від Drive).
        if (xhr.status === 200 || xhr.status === 201) {
          const meta = parseJsonResponse(xhr.responseText);
          const driveFileId = typeof meta.id === 'string' ? meta.id : null;
          if (!driveFileId) {
            reject(new ApiError(xhr.status, 'drive_missing_file_id', xhr.responseText));
            return;
          }
          resolve({
            completed: true,
            receivedBytes: args.totalBytes,
            totalBytes: args.totalBytes,
            driveFileId,
          });
          return;
        }
        // 404/410 — сесія померла на боці Drive
        if (xhr.status === 404 || xhr.status === 410) {
          reject(new ApiError(410, 'session_gone', null));
          return;
        }
        reject(new ApiError(xhr.status, 'drive_chunk_error', xhr.responseText));
        return;
      }

      // proxy-mode: наш сервер віддає JSON 200-х
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.response as PutChunkResponse);
        return;
      }
      const body: unknown = xhr.response;
      const code =
        typeof body === 'object' && body !== null && 'error' in body
          ? String((body as {error: unknown}).error)
          : 'http_error';
      reject(new ApiError(xhr.status, code, body));
    };
    xhr.onerror = () => reject(new ApiError(0, 'network_error', null));
    xhr.ontimeout = () => reject(new ApiError(0, 'timeout', null));
    xhr.onabort = () => reject(new DOMException('aborted', 'AbortError'));

    if (args.signal) {
      args.signal.addEventListener('abort', () => xhr.abort(), {once: true});
    }

    xhr.send(args.blob);
  });
}

// Транзієнтні: мережа, тайм-аут, 5xx, 429. 410 (session_gone) теж ретраїмо —
// при наступному ensureSession Idempotency-Key уже відрізнятиметься… Ні, key
// той самий, але запис у Mongo видалений (deleteOne у бекенді на 410), тож
// створюється свіжа Drive session. Перевірив бекенд: цей шлях коректний.
const RETRYABLE_STATUSES = new Set([0, 408, 410, 425, 429, 500, 502, 503, 504]);

function isRetryable(err: unknown): boolean {
  if (err instanceof ApiError) return RETRYABLE_STATUSES.has(err.status);
  return err instanceof Error && err.message.includes('chunk made no progress');
}

function backoffDelay(consecutiveErrors: number): number {
  const cap = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** (consecutiveErrors - 1));
  return Math.floor(Math.random() * cap);
}

function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}

// Спить до min(ms, online-подія, abort). `online`-подія firing-ить лише при
// переході offline→online — тож якщо ми вже були online і просто отримали
// 502, подія не зріже бекофф (буде тільки таймер).
function sleepUntilOnlineOrTimer(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('aborted', 'AbortError'));
      return;
    }
    const cleanup = (): void => {
      clearTimeout(timer);
      window.removeEventListener('online', onOnline);
      signal?.removeEventListener('abort', onAbort);
    };
    const onOnline = (): void => {
      cleanup();
      resolve();
    };
    const onAbort = (): void => {
      cleanup();
      reject(new DOMException('aborted', 'AbortError'));
    };
    const timer = setTimeout(() => {
      cleanup();
      resolve();
    }, ms);
    window.addEventListener('online', onOnline, {once: true});
    signal?.addEventListener('abort', onAbort, {once: true});
  });
}
